/**
 * PasswordWithConfirm - UI Layer
 * Contraseña + confirmación con checklist de reglas en vivo y mensaje de match.
 * `valid` = todas las reglas pasan + la contraseña no viola el charset de Cognito + confirm
 * no vacío + confirm === password.
 */

import { useEffect } from 'react';
import { Input } from '@/components/ui/Input';
import { buildPasswordRules, allRulesPass, cognitoRejectionReason } from '@/lib/passwordRules';
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy';

interface PasswordWithConfirmProps {
  password: string;
  confirm: string;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
  onValidChange?: (valid: boolean) => void;
  disabled?: boolean;
}

export function PasswordWithConfirm({
  password,
  confirm,
  onPassword,
  onConfirm,
  onValidChange,
  disabled,
}: PasswordWithConfirmProps) {
  const policy = usePasswordPolicy();
  const rules = buildPasswordRules(policy);

  const mismatch = confirm.length > 0 && confirm !== password;
  const rejection = cognitoRejectionReason(password);
  const valid =
    allRulesPass(rules, password) &&
    rejection === null &&
    confirm.length > 0 &&
    confirm === password;

  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);

  return (
    <div>
      <Input
        type="password"
        label="Contraseña"
        autoComplete="new-password"
        value={password}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(rejection !== null ? { error: rejection } : {})}
        onChange={(e) => onPassword(e.target.value)}
      />

      <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0' }}>
        {rules.map((r) => {
          const pass = r.test(password);
          return (
            <li key={r.label} style={{ color: pass ? 'green' : 'inherit' }}>
              <span aria-hidden="true">{pass ? '✓' : '○'}</span> {r.label}
            </li>
          );
        })}
      </ul>

      <Input
        type="password"
        label="Confirmar contraseña"
        autoComplete="new-password"
        value={confirm}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(mismatch ? { error: 'Las contraseñas no coinciden.' } : {})}
        onChange={(e) => onConfirm(e.target.value)}
      />
    </div>
  );
}
